import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  renegociacoesService, type FiltroRenegociacoes,
} from "../services/renegociacoesService";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/apiClient";
import type { RenegociarRequest } from "../types";

/**
 * Renegociar troca a dívida de lugar: os títulos antigos saem do a receber e o
 * acordo entra. Toda mutação invalida as telas de contas a receber — senão elas
 * seguem exibindo a dívida velha e alguém cobra o que já foi substituído.
 */
function useInvalidarFinanceiro() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["renegociacoes"] });
    queryClient.invalidateQueries({ queryKey: ["renegociacoes-candidatas"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber-parcelas"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber-gerencial-clientes"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber-gerencial-titulos"] });
    // A fatura compartilha o pool de parcelas elegíveis.
    queryClient.invalidateQueries({ queryKey: ["faturas-candidatas"] });
  };
}

/** Parcelas elegíveis do cliente. Só busca quando há cliente escolhido. */
export const useCandidatasRenegociacao = (clienteId: number | null) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["renegociacoes-candidatas", clienteId],
    queryFn: () => renegociacoesService.getCandidatas(clienteId!),
    enabled: !!clienteId,
  });

  return { parcelas: data ?? [], isLoading, isFetching };
};

export const useRenegociacoes = (filtro: FiltroRenegociacoes = {}) => {
  const { data, isLoading } = useQuery({
    queryKey: ["renegociacoes", filtro],
    queryFn: () => renegociacoesService.getAll(filtro),
    placeholderData: (prev) => prev,
  });

  return {
    acordos: data?.itens ?? [],
    total: data?.total ?? 0,
    totalPaginas: data?.totalPaginas ?? 0,
    isLoading,
  };
};

export const useRenegociacaoDetalhe = (id: number | null) => {
  const { data, isLoading } = useQuery({
    queryKey: ["renegociacoes", "detalhe", id],
    queryFn: () => renegociacoesService.getById(id!),
    enabled: id !== null,
  });

  return { detalhe: data, isLoading };
};

export const useRenegociacoesMutations = () => {
  const invalidar = useInvalidarFinanceiro();

  const renegociar = useMutation({
    mutationFn: (req: RenegociarRequest) => renegociacoesService.renegociar(req),
    onSuccess: (d) => {
      invalidar();
      const dif = d.acordo.valorRenegociado - d.acordo.valorOriginal;
      const fmt = (v: number) =>
        v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
      toast.success(
        `Acordo #${d.acordo.id} — ${d.acordo.qtdTitulosSubstituidos} título(s) substituído(s) por ` +
        `${fmt(d.acordo.valorRenegociado)}` +
        (dif !== 0
          ? ` (${dif > 0 ? "juros" : "desconto"} de ${fmt(Math.abs(dif))})`
          : ""),
      );
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const estornar = useMutation({
    mutationFn: (id: number) => renegociacoesService.estornar(id),
    onSuccess: () => {
      invalidar();
      toast.success("Renegociação estornada — os títulos antigos voltaram ao contas a receber");
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    renegociar: renegociar.mutate,
    isRenegociando: renegociar.isPending,
    estornarRenegociacao: estornar.mutate,
    isEstornando: estornar.isPending,
  };
};
